import moment from 'moment';

export function getTimeInterval(lastSeen: string): string {
  const utcDate = moment.utc(lastSeen);
  const localDate = utcDate.local();
  const now = moment();
  const diff = now.diff(localDate, 'seconds');

  if (diff < 0) {
    const futureDiff = Math.abs(diff); // Convert to a positive number for readability

    if (futureDiff < 60) {
      return `In ${futureDiff} second${futureDiff === 1 ? '' : 's'}`;
    }

    if (futureDiff < 3600) {
      const minutes = Math.floor(futureDiff / 60);
      return `In ${minutes} minute${minutes === 1 ? '' : 's'}`;
    }

    if (futureDiff < 86400) {
      // Less than 1 day
      const hours = Math.floor(futureDiff / 3600);
      return `In ${hours} hour${hours === 1 ? '' : 's'}`;
    }

    return `On ${localDate.format('DD MMM YYYY, hh:mm A')}`;
  }
  const intervals: [number, string][] = [
    [60, `${diff} second${diff === 1 ? '' : 's'} ago`], // Less than 1 minute ago
    [3600, `${Math.floor(diff / 60)} minute${Math.floor(diff / 60) === 1 ? '' : 's'} ago`], // Less than 1 hour ago
  ];

  // Less than an hour ago: show "X minutes ago"
  if (diff < 3600) {
    const [, label]: [number, string] = intervals.find(([interval]) => diff < interval)!;
    return label;
  }

  // If the event is today, but more than 1 hour ago, show the exact time (e.g., "10:50 AM")
  if (now.isSame(localDate, 'day')) {
    return `Today at ${localDate.format('hh:mm:ss A')}`;
  }

  // If it was yesterday, show "Yesterday at [time]"`
  const yesterday = moment().subtract(1, 'day');
  if (yesterday.isSame(localDate, 'day')) {
    return `Yesterday at ${localDate.format('hh:mm:ss A')}`;
  }

  // For dates older than yesterday, show the exact date and time (e.g., "10 October 2023, 10:50 AM")
  return localDate.format('DD MMM YYYY, hh:mm:ss A');
}

export function getDayTimeInterval(dateInput: Date | string): string {
  if (!dateInput) return 'N/A';

  try {
    const date = dateInput instanceof Date ? dateInput : new Date(dateInput);
    if (isNaN(date.getTime())) return 'N/A';

    const diff: number = Math.floor((new Date().getTime() - date.getTime()) / 1000);
    const intervals: [number, string][] = [
      [60, `${diff} second${diff === 1 ? '' : 's'} ago`],
      [3600, `${Math.floor(diff / 60)} minute${Math.floor(diff / 60) === 1 ? '' : 's'} ago`],
      [86400, `${Math.floor(diff / 3600)} hour${Math.floor(diff / 3600) === 1 ? '' : 's'} ago`],
      [Infinity, `${Math.floor(diff / 86400)} day${Math.floor(diff / 86400) === 1 ? '' : 's'} ago`],
    ];

    const [, label]: [number, string] = intervals.find(([interval]) => diff < interval) || [
      0,
      'N/A',
    ];
    return label;
  } catch (error) {
    console.warn('Error parsing date:', error);
    return 'N/A';
  }
}
